import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Activity } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  return String(detail);
}

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin", icon: ShieldCheck, desc: "Manage all matches and users" },
  { value: "scorer", label: "Scorer / Umpire", icon: Activity, desc: "Create & score your own matches" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("scorer");
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (role === "admin" && !inviteCode.trim()) {
      toast.error("Admin invite code is required");
      return;
    }
    setBusy(true);
    try {
      await register(email.trim(), password, name.trim(), role, inviteCode.trim());
      toast.success("Account created — welcome!");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-8 py-16" data-testid="register-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Get started</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-2">
        Pick up the bat
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Create an account to score matches. Admins manage all matches; scorers / umpires record ball-by-ball. Spectators don't need to sign up — just open any match link.
      </p>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Your name" type="text" value={name} onChange={setName} testId="register-name" required />
        <Field label="Email" type="email" value={email} onChange={setEmail} testId="register-email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} testId="register-password" required />

        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Role</label>
          <div className="space-y-2 mt-2">
            {ROLE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = role === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setRole(opt.value)}
                  data-testid={`register-role-${opt.value}`}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 border transition-colors ${
                    selected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-foreground/30"
                  }`}
                  style={{ borderRadius: 2 }}
                >
                  <Icon className={`w-5 h-5 ${selected ? "text-primary" : "text-muted-foreground"}`} strokeWidth={2.5} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-display font-black uppercase text-sm tracking-tight ${selected ? "text-primary" : "text-foreground"}`}>
                      {opt.label}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{opt.desc}</div>
                  </div>
                  <span
                    className={`w-3 h-3 border ${selected ? "bg-primary border-primary" : "border-border"}`}
                    aria-hidden
                  />
                </button>
              );
            })}
          </div>
          {role === "admin" && (
            <div className="mt-4 border border-destructive/50 p-3" style={{ borderRadius: 2 }}>
              <label className="text-xs uppercase tracking-[0.3em] text-destructive font-bold">Admin Invite Code</label>
              <input
                type="password"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                data-testid="register-invite-code"
                placeholder="Enter the secret code"
                className="w-full bg-transparent border border-border focus:border-destructive focus:outline-none px-3 py-2 mt-2 font-mono text-sm"
              />
              <p className="mt-2 text-[11px] text-muted-foreground">
                Required to register as admin. Ask the existing admin or check your <code className="text-foreground">.env</code> for <code className="text-foreground">ADMIN_INVITE_CODE</code>.
              </p>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit-btn"
          className="tactical-btn w-full bg-primary text-primary-foreground hover:bg-[#C9E620] disabled:opacity-50 px-6 py-4 inline-flex items-center justify-center gap-2 text-sm"
        >
          {busy ? "Creating..." : "Create Account"} <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-primary hover:underline font-bold" data-testid="login-link">
          Sign in
        </Link>
      </p>
    </div>
  );
}

function Field({ label, type, value, onChange, testId, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        required={required}
        className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-4 py-3 mt-2 font-mono text-sm"
      />
    </div>
  );
}
